import { useState, useRef, useEffect } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { Mic, Square, Upload, Loader2, FileText, Trash2, Plus, ListTodo, ClipboardList, ChevronDown, ChevronUp, Share2, Send, ShieldAlert, Check, UserPlus, X, CheckCircle2 } from "lucide-react";
import { fDateTime } from "@/lib/format";
import { toast } from "sonner";

function Recorder({ title, onDone }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await upload(blob, "inspelning.webm");
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) { toast.error("Kunde inte komma åt mikrofonen"); }
  };
  const stop = () => {
    if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); clearInterval(timerRef.current); }
  };
  const upload = async (blob, name) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, name);
      fd.append("title", title || "Möte");
      const r = await api.post("/work/meetings/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      toast.success("Möte transkriberat");
      onDone(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const onFile = async (e) => { const f = e.target.files?.[0]; if (f) await upload(f, f.name); e.target.value = ""; };
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="recorder">
      {!recording ? (
        <Button onClick={start} disabled={busy} data-testid="rec-start"><Mic className="mr-1 h-4 w-4" />Spela in</Button>
      ) : (
        <Button variant="destructive" onClick={stop} data-testid="rec-stop"><Square className="mr-1 h-4 w-4" />Stoppa ({mm}:{ss})</Button>
      )}
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-secondary" data-testid="rec-upload">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Ladda upp ljudfil
        <input type="file" accept="audio/*,.m4a,.mp3,.wav,.webm" className="hidden" onChange={onFile} disabled={busy} data-testid="rec-file-input" />
      </label>
      {busy && <span className="text-xs text-muted-foreground">Transkriberar…</span>}
    </div>
  );
}

function MeetingCard({ m, onChanged }) {
  const [open, setOpen] = useState(false);
  const [prep, setPrep] = useState(false);
  const addTasks = async () => {
    const titles = m.suggested_tasks || [];
    if (!titles.length) return;
    try { await api.post("/work/tasks/bulk", { titles, source: "meeting", meeting_id: m.id }); toast.success(`${titles.length} uppgifter tillagda i Struktur`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => { if (!window.confirm("Ta bort mötet?")) return; try { await api.delete(`/work/meetings/${m.id}`); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const statusLabel = { draft: "Utkast", in_review: "Under granskning", approved: "Godkänt", changes_requested: "Ändring begärd", published: "Publicerat" };
  return (
    <Card className="p-4" data-testid={`meeting-${m.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="h-4 w-4 text-accent" />
        <span className="font-medium">{m.title}</span>
        <Badge variant="outline">{m.source === "recording" ? "inspelning" : "anteckningar"}</Badge>
        <span className="ml-auto text-xs text-muted-foreground">{fDateTime(m.created_at)}</span>
      </div>
      {m.summary && <p className="mt-2 text-sm text-muted-foreground" data-testid={`meeting-sum-${m.id}`}>{m.summary}</p>}
      {(m.suggested_tasks || []).length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {m.suggested_tasks.map((tk, i) => <Badge key={i} variant="secondary" data-testid={`meeting-task-${m.id}-${i}`}>{tk}</Badge>)}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {(m.suggested_tasks || []).length > 0 && <Button size="sm" onClick={addTasks} data-testid={`meeting-addtasks-${m.id}`}><ListTodo className="mr-1 h-4 w-4" />Lägg till i Struktur</Button>}
        <Button size="sm" variant="outline" onClick={() => setPrep((p) => !p)} data-testid={`meeting-prep-${m.id}`}><Share2 className="mr-1 h-4 w-4" />Förbered &amp; dela</Button>
        {m.review_status && m.review_status !== "draft" && <Badge variant={m.review_status === "published" ? "default" : "secondary"} data-testid={`meeting-status-${m.id}`}>{statusLabel[m.review_status] || m.review_status}</Badge>}
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid={`meeting-toggle-${m.id}`}>
          {open ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}Protokoll & transkribering
        </Button>
        {m.audio_path && <a href={`${API}/files/${m.audio_path}`} target="_blank" rel="noreferrer" className="text-sm text-accent underline" data-testid={`meeting-audio-${m.id}`}>Lyssna</a>}
        <Button size="icon" variant="ghost" className="ml-auto" onClick={del} data-testid={`meeting-del-${m.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
      {open && (
        <div className="mt-3 space-y-3 border-t pt-3" data-testid={`meeting-detail-${m.id}`}>
          {m.protocol && (<div><div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><ClipboardList className="h-3.5 w-3.5" />Mötesprotokoll</div><pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">{m.protocol}</pre></div>)}
          {m.transcript && (<div><div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Transkribering</div><p className="whitespace-pre-wrap text-sm text-muted-foreground">{m.transcript}</p></div>)}
        </div>
      )}
      {prep && <SharePanel m={m} onChanged={onChanged} />}
    </Card>
  );
}

function SharePanel({ m, onChanged }) {
  const { data: colleagues } = useApi("/work/colleagues");
  const [attendees, setAttendees] = useState(m.attendees || []);
  const [items, setItems] = useState(m.publish_items?.length ? m.publish_items : (m.decisions || []));
  const [flags, setFlags] = useState(m.sensitive_flags || []);
  const [pickUser, setPickUser] = useState("");
  const [busy, setBusy] = useState("");
  const status = m.review_status || "draft";
  const reviewers = attendees.filter((a) => a.user_id);
  const allApproved = reviewers.length > 0 && reviewers.every((a) => a.status === "approved");
  const CAT = { personuppgift: "Personuppgift", art9: "Särskild kategori (art.9)", konfidentiellt: "Konfidentiellt" };

  const setItem = (i, v) => setItems((x) => x.map((it, j) => (j === i ? v : it)));
  const addColleague = () => {
    const c = (colleagues || []).find((x) => x.user_id === pickUser);
    if (!c || attendees.some((a) => a.user_id === c.user_id)) return;
    setAttendees((a) => [...a, { name: c.name, user_id: c.user_id, status: "pending" }]);
    setPickUser("");
  };
  const suggest = async () => {
    setBusy("suggest");
    try {
      const r = await api.post(`/work/meetings/${m.id}/suggest-attendees`);
      const ex = new Set(attendees.map((a) => a.user_id || a.name));
      const add = (r.data.suggestions || []).filter((s) => !ex.has(s.user_id || s.name)).map((s) => ({ ...s, status: "pending" }));
      setAttendees((a) => [...a, ...add]);
      toast.success(`${add.length} deltagarförslag från AI`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const savePrep = async () => {
    await api.put(`/work/meetings/${m.id}/prepare`, { attendees: attendees.map((a) => ({ name: a.name, user_id: a.user_id })), publish_items: items.filter((x) => (x || "").trim()) });
  };
  const doSave = async () => { setBusy("save"); try { await savePrep(); toast.success("Utkast sparat"); onChanged(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); } };
  const scan = async () => {
    setBusy("scan");
    try { const r = await api.post(`/work/meetings/${m.id}/scan-sensitive`, { text: items.join("\n") }); setFlags(r.data.flags || []); toast.message((r.data.flags || []).length ? `${r.data.flags.length} känsliga partier – granska & maska` : "Inga känsliga partier hittades"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const sendReview = async () => {
    setBusy("send");
    try { await savePrep(); await api.post(`/work/meetings/${m.id}/send-review`); toast.success("Skickat för granskning till deltagarna"); onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const publish = async () => {
    const needsForce = !allApproved;
    let reason = "";
    if (needsForce) { reason = window.prompt("Alla deltagare har inte godkänt. Ange motivering för att publicera ändå (loggas):") || ""; if (!reason.trim()) return; }
    setBusy("publish");
    try { await api.post(`/work/meetings/${m.id}/publish`, { publish_target: "intern", force: needsForce, force_reason: reason }); toast.success(needsForce ? "Publicerat (forcerat – loggat)" : "Beslut publicerade i flödet"); onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  if (status === "published") {
    return <div className="mt-3 rounded-lg border-t bg-emerald-50 p-3 text-sm text-emerald-800" data-testid={`share-published-${m.id}`}><CheckCircle2 className="mr-1 inline h-4 w-4" />Besluten är publicerade i flödet{m.forced ? " (forcerat – motivering loggad)" : " efter alla deltagares godkännande"}.</div>;
  }
  return (
    <div className="mt-3 space-y-4 border-t pt-3" data-testid={`share-panel-${m.id}`}>
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Beslut att publicera (endast dessa delas – inte hela loggen)</div>
        {items.map((it, i) => (
          <div key={i} className="mb-1 flex items-center gap-2">
            <Input value={it} onChange={(e) => setItem(i, e.target.value)} data-testid={`share-item-${m.id}-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => setItems((x) => x.filter((_, j) => j !== i))} data-testid={`share-item-rm-${m.id}-${i}`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setItems((x) => [...x, ""])} data-testid={`share-item-add-${m.id}`}><Plus className="mr-1 h-4 w-4" />Beslut</Button>
          <Button size="sm" variant="outline" onClick={scan} disabled={busy === "scan"} data-testid={`share-scan-${m.id}`}>{busy === "scan" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-1 h-4 w-4" />}Scanna känslig info</Button>
        </div>
        {flags.length > 0 && (
          <div className="mt-2 space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-2" data-testid={`share-flags-${m.id}`}>
            <div className="text-xs font-semibold text-amber-800">AI föreslår att maska (GDPR/AI-akt – du beslutar):</div>
            {flags.map((f, i) => (
              <div key={i} className="text-xs text-amber-900" data-testid={`share-flag-${m.id}-${i}`}><Badge variant="outline" className="mr-1">{CAT[f.category] || f.category}</Badge>"{f.text}" – {f.reason}</div>
            ))}
            <div className="text-[11px] text-amber-700">Redigera besluten ovan för att maska/ta bort känsliga uppgifter.</div>
          </div>
        )}
      </div>

      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Deltagare som ska godkänna</div>
        <div className="mb-2 flex flex-wrap gap-2">
          <Select value={pickUser} onValueChange={setPickUser}>
            <SelectTrigger className="h-9 w-56" data-testid={`share-colleague-select-${m.id}`}><SelectValue placeholder="Välj kollega" /></SelectTrigger>
            <SelectContent>{(colleagues || []).map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={addColleague} data-testid={`share-add-colleague-${m.id}`}><UserPlus className="mr-1 h-4 w-4" />Lägg till</Button>
          <Button size="sm" variant="outline" onClick={suggest} disabled={busy === "suggest"} data-testid={`share-suggest-${m.id}`}>{busy === "suggest" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-1 h-4 w-4" />}Föreslå (AI)</Button>
        </div>
        <div className="space-y-1">
          {attendees.map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-sm" data-testid={`share-attendee-${m.id}-${i}`}>
              <span className="flex-1">{a.name}{!a.user_id && <span className="text-xs text-muted-foreground"> (saknar konto – kan ej granska)</span>}</span>
              <Badge variant={a.status === "approved" ? "default" : a.status === "declined" ? "destructive" : "secondary"}>{a.status === "approved" ? "godkänt" : a.status === "declined" ? "ändring begärd" : "väntar"}</Badge>
              <Button size="icon" variant="ghost" onClick={() => setAttendees((x) => x.filter((_, j) => j !== i))} data-testid={`share-attendee-rm-${m.id}-${i}`}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          {!attendees.length && <p className="text-xs text-muted-foreground">Inga deltagare valda ännu.</p>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={doSave} disabled={busy === "save"} data-testid={`share-save-${m.id}`}>Spara utkast</Button>
        <Button size="sm" onClick={sendReview} disabled={busy === "send"} data-testid={`share-send-${m.id}`}>{busy === "send" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}Skicka för granskning</Button>
        <Button size="sm" onClick={publish} disabled={busy === "publish"} className={allApproved ? "" : "opacity-90"} data-testid={`share-publish-${m.id}`}>{busy === "publish" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Share2 className="mr-1 h-4 w-4" />}{allApproved ? "Publicera i flödet" : "Publicera ändå…"}</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Endast de färdiga besluten publiceras. Granskning, godkännanden och ev. forcerad publicering loggas (GDPR/EU:s AI-akt – AI ger endast förslag, du fattar beslutet).</p>
    </div>
  );
}

function ReviewQueue({ onChanged }) {
  const { data, reload } = useApi("/work/meetings/reviews");
  const list = data || [];
  if (!list.length) return null;
  const act = async (mt, decision) => {
    let comment = "";
    if (decision === "decline") comment = window.prompt("Vad behöver ändras? (valfritt)") || "";
    try { await api.post(`/work/meetings/${mt.id}/review`, { decision, comment }); toast.success(decision === "approve" ? "Godkänt" : "Skickat tillbaka"); reload(); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mb-5 border-accent/40 p-4" data-testid="review-queue">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><ShieldAlert className="h-4 w-4 text-accent" />Väntar på din granskning ({list.length})</div>
      <div className="space-y-3">
        {list.map((mt) => (
          <div key={mt.id} className="rounded-lg border p-3" data-testid={`review-${mt.id}`}>
            <div className="font-medium">{mt.title}</div>
            <div className="mt-1 text-xs text-muted-foreground">Beslut som föreslås publiceras i flödet:</div>
            <ul className="mt-1 list-disc pl-5 text-sm">{(mt.publish_items || []).map((it, i) => <li key={i}>{it}</li>)}</ul>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => act(mt, "approve")} data-testid={`review-approve-${mt.id}`}><Check className="mr-1 h-4 w-4" />Godkänn</Button>
              <Button size="sm" variant="outline" onClick={() => act(mt, "decline")} data-testid={`review-decline-${mt.id}`}>Begär ändring</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function NotesComposer({ onDone }) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const r = await api.post("/work/meetings/notes", { title: title || "Möte", text }); toast.success("Mötesprotokoll skapat"); setText(""); setTitle(""); onDone(r.data); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="mb-5 space-y-3 p-4" data-testid="notes-card">
      <div className="text-sm font-semibold">Eller klistra in mötesanteckningar</div>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Mötestitel (valfritt)" data-testid="notes-title" />
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Klistra in anteckningar – AI:n gör sammanfattning, protokoll och att-göra-punkter." data-testid="notes-text" />
      <Button onClick={save} disabled={busy || !text.trim()} data-testid="notes-save">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}Skapa protokoll</Button>
    </Card>
  );
}

export default function Moten() {
  const { data: meetings, reload } = useApi("/work/meetings");
  const [title, setTitle] = useState("");
  const list = meetings || [];
  const onDone = () => { reload(); };
  return (
    <div className="mx-auto max-w-3xl" data-testid="moten-page">
      <PageHeader title="Möten & inspelning" icon={Mic} subtitle="Spela in eller ladda upp möten, få dem transkriberade och omvandlade till protokoll och att-göra-punkter." />
      <ReviewQueue onChanged={reload} />      <Card className="mb-5 space-y-3 p-4" data-testid="record-card">
        <Label>Mötestitel</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="t.ex. Veckoavstämning kund X" data-testid="record-title" />
        <Recorder title={title} onDone={onDone} />
        <p className="text-xs text-muted-foreground">Inspelningen transkriberas automatiskt (svenska). Max 25 MB per fil.</p>
      </Card>
      <NotesComposer onDone={onDone} />
      {list.length === 0 ? (
        <EmptyState icon={FileText} title="Inga möten än" text="Spela in, ladda upp eller klistra in anteckningar för att skapa ditt första mötesprotokoll." />
      ) : (
        <div className="space-y-3" data-testid="meetings-list">
          {list.map((m) => <MeetingCard key={m.id} m={m} onChanged={reload} />)}
        </div>
      )}
    </div>
  );
}
